"use client";

// Error boundaries must be Client Components
import { ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/nextjs";
import Error from "next/error";
import { useEffect } from "react";

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & {
        digest?: string;
        message?: string;
        stack?: string;
    };
    reset: () => void;
}) {
    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return (
        // global-error must include html and body tags
        <html lang="en" className="h-full">
            <body className="h-full antialiased bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
                <div className="min-h-full flex flex-col items-center justify-center p-4 md:p-8">
                    <div className="w-full max-w-md rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 relative">
                        {/* Gradient header similar to dashboard */}
                        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6 text-white relative overflow-hidden">
                            <div className="absolute inset-0 opacity-20">
                                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiPjxwYXRoIGQ9Ik0zNiAxOGMxLjIgMCAyLjEgMSAyLjEgMi4ycy0uOSAyLjItMi4xIDIuMmMtMS4yIDAtMi4xLTEtMi4xLTIuMnMuOS0yLjIgMi4xLTIuMnptLTEyIDI0YzEuMiAwIDIuMSAxIDIuMSAyLjJzLS45IDIuMi0yLjEgMi4yYy0xLjIgMC0yLjEtMS0yLjEtMi4ycy45LTIuMiAyLjEtMi4yek0zNiA0MmMxLjIgMCAyLjEgMSAyLjEgMi4ycy0uOSAyLjItMi4xIDIuMmMtMS4yIDAtMi4xLTEtMi4xLTIuMnMuOS0yLjIgMi4xLTIuMnptLTEyLTI0YzEuMiAwIDIuMSAxIDIuMSAyLjJzLS45IDIuMi0yLjEgMi4yYy0xLjIgMC0yLjEtMS0yLjEtMi4ycy45LTIuMiAyLjEtMi4yek0xMiAxOGMxLjIgMCAyLjEgMSAyLjEgMi4ycy0uOSAyLjItMi4xIDIuMmMtMS4yIDAtMi4xLTEtMi4xLTIuMnMuOS0yLjIgMi4xLTIuMnptMCAyNGMxLjIgMCAyLjEgMSAyLjEgMi4ycy0uOSAyLjItMi4xIDIuMmMtMS4yIDAtMi4xLTEtMi4xLTIuMnMuOS0yLjIgMi4xLTIuMnptMjQgMGMxLjIgMCAyLjEgMSAyLjEgMi4ycy0uOSAyLjItMi4xIDIuMmMtMS4yIDAtMi4xLTEtMi4xLTIuMnMuOS0yLjIgMi4xLTIuMnptMC0yNGMxLjIgMCAyLjEgMSAyLjEgMi4ycy0uOSAyLjItMi4xIDIuMmMtMS4yIDAtMi4xLTEtMi4xLTIuMnMuOS0yLjIgMi4xLTIuMnoiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
                            </div>
                            <div className="flex items-center relative z-10">
                                <div className="bg-white/20 p-2 rounded-full mr-3 shadow-inner">
                                    <SparklesIcon className="h-5 w-5 text-white" />
                                </div>
                                <h1 className="text-2xl font-bold font-satoshi">
                                    RTHM
                                </h1>
                            </div>
                        </div>

                        <div className="p-6">
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-3 font-satoshi">
                                We encountered an issue
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4 font-suisse">
                                Our team has been notified and is working to
                                resolve this problem. Please try again or return
                                to the dashboard.
                            </p>

                            {/* Error details section */}
                            <details className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-sm">
                                <summary className="font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                                    Technical details
                                </summary>
                                <div className="mt-3 space-y-2">
                                    {error.message && (
                                        <div>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                Error:
                                            </span>{" "}
                                            <span className="text-red-600 dark:text-red-400 font-mono">
                                                {error.message}
                                            </span>
                                        </div>
                                    )}
                                    {error.digest && (
                                        <div>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                Digest:
                                            </span>{" "}
                                            <span className="font-mono text-gray-600 dark:text-gray-400">
                                                {error.digest}
                                            </span>
                                        </div>
                                    )}
                                    {error.stack && (
                                        <div>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                Stack:
                                            </span>
                                            <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-800 rounded overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-300">
                                                {error.stack
                                                    .split("\n")
                                                    .slice(0, 5)
                                                    .join("\n")}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </details>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => reset()}
                                    className="py-2.5 min-h-12 px-4 rounded-full border flex justify-center items-center bg-cyan-800 text-white dark:bg-cyan-700 border-none transition-colors duration-200 cursor-pointer"
                                >
                                    <div className="flex flex-row items-center justify-center">
                                        <div className="leading-[14.30px] font-[599] font-satoshi tracking-wide text-base">
                                            Try again
                                        </div>
                                        <div className="flex items-center justify-center ml-2">
                                            <ArrowPathIcon className="h-5 w-5" />
                                        </div>
                                    </div>
                                </button>

                                <a
                                    href="/intelligence/dashboard"
                                    className="py-2.5 min-h-12 px-4 rounded-full border flex justify-center items-center border-cyan-800 text-cyan-800 dark:border-white dark:text-white transition-colors duration-200 cursor-pointer"
                                >
                                    <div className="flex flex-row items-center justify-center">
                                        <div className="leading-[14.30px] font-[599] font-satoshi tracking-wide text-base">
                                            Return to dashboard
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </body>
        </html>
    );
}
